import { useState, useEffect, useCallback } from 'react';
import { coreApi } from '@shared/api/client';
import { OAuthAppItem, CreateOAuthAppInput } from './types';

const INITIAL_FALLBACK_APPS: OAuthAppItem[] = [
  {
    id: 'app-1',
    name: 'Slack Connect Bot',
    client_id: 'client_hl_slk_991823',
    masked_secret: 'sec_hl_••••••••33b8',
    redirect_uris: ['https://slack.com/oauth/v2/callback'],
    allowed_scopes: ['contacts:read', 'notifications:write'],
    created_at: new Date(Date.now() - 30 * 86400000).toISOString(),
    last_rotated_at: new Date(Date.now() - 10 * 86400000).toISOString(),
    status: 'active',
  },
  {
    id: 'app-2',
    name: 'Google Workspace Directory Sync',
    client_id: 'client_hl_gws_441029',
    masked_secret: 'sec_hl_••••••••fa90',
    redirect_uris: ['https://accounts.google.com/o/oauth2/auth'],
    allowed_scopes: ['users:read', 'directory:sync'],
    created_at: new Date(Date.now() - 60 * 86400000).toISOString(),
    last_rotated_at: new Date(Date.now() - 60 * 86400000).toISOString(),
    status: 'active',
  },
];

export function useOAuthApps() {
  const [apps, setApps] = useState<OAuthAppItem[]>(INITIAL_FALLBACK_APPS);
  const [isLoading, setIsLoading] = useState(true);
  const [newlyCreatedSecret, setNewlyCreatedSecret] = useState<string | null>(null);

  const fetchApps = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await coreApi.get<OAuthAppItem[]>('/api/oauth-apps');
      if (Array.isArray(data)) {
        setApps(data);
      } else {
        setApps(INITIAL_FALLBACK_APPS);
      }
    } catch {
      setApps(INITIAL_FALLBACK_APPS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  const createApp = async (input: CreateOAuthAppInput) => {
    try {
      const res = await coreApi.post<OAuthAppItem>('/api/oauth-apps', input);
      if (res && res.raw_secret) {
        setNewlyCreatedSecret(res.raw_secret);
        setApps((prev) => [res, ...prev]);
        return res;
      }
    } catch {
      // fallback
    }
    const mockRaw = `sec_hl_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
    const mockApp: OAuthAppItem = {
      id: `app-${Date.now()}`,
      name: input.name,
      client_id: `client_hl_${Date.now()}`,
      masked_secret: `sec_hl_••••••••${mockRaw.slice(-4)}`,
      raw_secret: mockRaw,
      redirect_uris: input.redirect_uris,
      allowed_scopes: input.allowed_scopes,
      created_at: new Date().toISOString(),
      last_rotated_at: new Date().toISOString(),
      status: 'active',
    };
    setNewlyCreatedSecret(mockRaw);
    setApps((prev) => [mockApp, ...prev]);
    return mockApp;
  };

  const rotateSecret = async (id: string) => {
    try {
      const res = await coreApi.post<OAuthAppItem>(`/api/oauth-apps/${id}/rotate-secret`, {});
      if (res && res.raw_secret) {
        setNewlyCreatedSecret(res.raw_secret);
        setApps((prev) => prev.map((a) => (a.id === id ? res : a)));
        return res;
      }
    } catch {
      // fallback
    }
    const mockRaw = `sec_hl_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
    setNewlyCreatedSecret(mockRaw);
    setApps((prev) =>
      prev.map((a) =>
        a.id === id
          ? {
              ...a,
              masked_secret: `sec_hl_••••••••${mockRaw.slice(-4)}`,
              raw_secret: mockRaw,
              last_rotated_at: new Date().toISOString(),
            }
          : a
      )
    );
  };

  const deleteApp = async (id: string) => {
    try {
      await coreApi.delete(`/api/oauth-apps/${id}`);
    } catch {
      // fallback
    }
    setApps((prev) => prev.filter((a) => a.id !== id));
  };

  return {
    apps,
    isLoading,
    newlyCreatedSecret,
    clearNewSecret: () => setNewlyCreatedSecret(null),
    createApp,
    rotateSecret,
    deleteApp,
    refresh: fetchApps,
  };
}

export default useOAuthApps;
